import { summariseConditions, type Condition } from '@hc/shared';
import {
  draftMemoNarrative,
  type MemoNarrativeInput,
  type PlatformBuilderItem,
  type PlatformBuilderResult,
} from '../api/client.js';
import type { ScreenerRow } from '../api/screener-client.js';
import {
  SCREENER_RIGHT_COLUMN_IDS,
  renderCell,
  type ScreenerRightColumnId,
} from './screener-columns.js';
import { convertFromEur, CURRENCY_SYMBOL, type GlobalCurrency } from '../currency.js';
import { formatMoney, EMPTY_VALUE } from '../format.js';
import { staticPinMapHtml } from './dossier.js';

// Export helpers for a Platform Builder (Prescription) build: a CSV of the
// buy-list with every screener column, and a print-ready INVESTMENT COMMITTEE
// MEMORANDUM — an IC-style narrative document rendered in the same paper-sheet
// grammar as the Target Dossier.
//
// The memo has two prose layers:
//   1. DETERMINISTIC — every sentence assembled from platform evidence
//      (conditions, scores, the consolidation benchmark). Always available,
//      renders instantly, and is the fallback for everything below.
//   2. DRAFTED — the API's narrative layer (claude-opus-4-8) redrafts the
//      deterministic sections as analyst prose. The server validates every
//      drafted section against the fact corpus (figures ⊆ facts, names ⊆
//      buy-list, no claim language) and returns only survivors; the window
//      upgrades those sections in place. The model writes PROSE, never claims.

export interface BuildExportMeta {
  country: string;
  subsegment: string;
  region: string | null;
  objectives: string[];
  anchorMode: string;
  /** Whether conditions were computed buyer-relative or from the form alone —
   *  the memo must state which (spec §12.1). */
  contextMode: 'buyer_aware' | 'form_context';
  mandateName: string | null;
  portfolioName: string | null;
}

const slug = (s: string) =>
  s
    .replace(/\W+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function csvCell(v: string | number | null | undefined): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV of the target entities with the full screener column set (rendered via the
 *  same cell renderer the Screener/Watchlist use, so the data matches 1:1). */
export function exportPortfolioCsv(
  rows: ScreenerRow[],
  currency: GlobalCurrency,
  label: (id: ScreenerRightColumnId) => string,
  name: string,
): void {
  const clean = (s: string) => (s === EMPTY_VALUE ? '' : s);
  const cols = SCREENER_RIGHT_COLUMN_IDS;
  const header = ['Entity', 'Company number', ...cols.map(label)];
  const lines = [header.map(csvCell).join(',')];
  for (const row of rows) {
    lines.push(
      [
        row.companyName ?? '',
        row.companyNumber ?? '',
        ...cols.map((id) => clean(renderCell(row, id, currency).display)),
      ]
        .map(csvCell)
        .join(','),
    );
  }
  const csv = '﻿' + lines.join('\r\n');
  const date = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `platform-build-${slug(name)}-${date}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Open the memo: deterministic document instantly, then upgrade the narrative
 *  sections in place when (and only when) the drafted prose passes the
 *  server-side fact validator. Any failure leaves the deterministic memo. */
export function openPortfolioInvestmentMemo(
  result: PlatformBuilderResult,
  meta: BuildExportMeta,
  ccy: GlobalCurrency,
): void {
  const w = window.open('', '_blank');
  if (!w) return;
  const composed = composeMemo(result, meta, ccy);
  w.document.write(renderHtml(composed, null, { drafting: true }));
  w.document.close();

  const input: MemoNarrativeInput = {
    factCorpus: composed.factCorpus,
    allowedNames: composed.allowedNames,
    sections: composed.sections,
    contextMode: meta.contextMode,
    mandateName: meta.mandateName,
    portfolioName: meta.portfolioName,
    subsegment: meta.subsegment,
    geo: composed.geo,
  };
  draftMemoNarrative(input)
    .then((res) => {
      if (w.closed || res.sections.length === 0) {
        removeDraftPill(w);
        return;
      }
      const overrides = new Map(res.sections.map((s) => [s.key, s.text]));
      w.document.open();
      w.document.write(renderHtml(composed, overrides, { drafting: false, model: res.model }));
      w.document.close();
    })
    .catch(() => {
      // 503 (no key), model failure, network — the deterministic memo stands.
      removeDraftPill(w);
    });
}

function removeDraftPill(w: Window): void {
  if (w.closed) return;
  try {
    w.document.getElementById('draftpill')?.remove();
  } catch {
    // window navigated away — nothing to clean
  }
}

/** Locator map for the build — the SAME real basemap as the Record dossier
 *  (CARTO Positron tiles + projected pins), not a schematic box. Anchor pin is
 *  emphasised; bolt-ons are accent dots. */
function mapBlock(result: PlatformBuilderResult): string {
  const pins = result.portfolio
    .filter((p) => p.latitude != null && p.longitude != null)
    .map((p) => ({
      lat: p.latitude as number,
      lng: p.longitude as number,
      anchor: p.role === 'platform',
    }));
  const total = result.portfolio.length;
  if (pins.length === 0) {
    return `<div class="note">No mapped locations for these targets.</div>`;
  }
  return `${staticPinMapHtml(pins, { width: 300, height: 224 })}
  <div class="note">${pins.length} of ${total} located · ◆ anchor · ● bolt-on (registered-office or site centroid)</div>`;
}

// ── Narrative helpers ────────────────────────────────────────────────────────
// Deterministic sentence assembly. Everything reads FROM the data; branches
// select phrasing, they never invent facts.

const listJoin = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const plural = (n: number, one: string, many?: string) => (n === 1 ? one : (many ?? one + 's'));

const stripDot = (s: string) => s.replace(/\.$/, '');

const isReportedBasis = (basis: string | null | undefined) =>
  (basis ?? '').trim().toLowerCase() === 'reported';

const avgOf = (vals: Array<number | null>): number | null => {
  const ns = vals.filter((v): v is number => v != null);
  return ns.length ? Math.round(ns.reduce((a, b) => a + b, 0) / ns.length) : null;
};

const paras = (text: string): string =>
  text
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p.trim())}</p>`)
    .join('');

// ── Composition — every deterministic narrative unit + the fact corpus ──────

interface ComposedMemo {
  result: PlatformBuilderResult;
  meta: BuildExportMeta;
  ccy: GlobalCurrency;
  geo: string;
  /** Narrative units by section key (the draftable prose). */
  narrative: Map<string, string>;
  pillarTitles: string[];
  burden: string;
  targetMeta: Map<string, string>; // entityId → "(London · €804.26K modelled P51)"
  /** Everything the drafting layer may state — sections + evidence strings. */
  factCorpus: string[];
  allowedNames: string[];
  sections: Array<{ key: string; text: string }>;
}

function composeMemo(
  result: PlatformBuilderResult,
  meta: BuildExportMeta,
  ccy: GlobalCurrency,
): ComposedMemo {
  const s = result.summary;
  const targets = result.portfolio;
  const n = targets.length;
  const money = (eur: number | null | undefined) =>
    eur == null ? '—' : formatMoney(convertFromEur(eur, ccy), { currency: CURRENCY_SYMBOL[ccy] });
  const fillPct = Math.round((s.assembledEur / Math.max(1, s.targetEur)) * 100);
  const geo = meta.region ?? s.coreRegion ?? meta.country;
  const regions = [...new Set(targets.map((p) => p.region).filter((r): r is string => !!r))];
  const buyerAware = meta.contextMode === 'buyer_aware';

  const buildConditions = result.conditions ?? [];
  const condSummary = summariseConditions(buildConditions);
  const targetsWith = (token: string) =>
    targets.filter((p) => (p.conditions ?? []).some((c) => c.token === token));
  const successionRich = targetsWith('succession_rich');
  const localChampions = targetsWith('local_champion');
  const hiddenPlatforms = targetsWith('hidden_platform');
  const gapTargets = targetsWith('footprint_gap');
  const overlapTargets = targets.filter((p) =>
    (p.conditions ?? []).some((c) => c.token === 'cannibalisation_risk'),
  );
  const avgSeller = avgOf(targets.map((p) => p.sellerReadiness));
  const avgOrig = avgOf(targets.map((p) => p.originationSignal));
  const reportedCount = targets.filter((p) => isReportedBasis(p.turnoverBasis)).length;
  const modelledCount = n - reportedCount;
  const anchor = targets.find((p) => p.role === 'platform') ?? null;
  const boltOns = targets.filter((p) => p.role === 'bolton');
  const boltRevs = boltOns
    .map((b) => b.turnoverEur)
    .filter((v): v is number => v != null)
    .sort((a, b) => a - b);
  const boltMedian = boltRevs.length ? boltRevs[Math.floor((boltRevs.length - 1) / 2)]! : null;
  const weakCount = targets.filter((p) => p.qualityTier === 'Weak').length;
  const unratedCount = targets.filter(
    (p) => p.qualityTier == null || p.qualityTier === 'Not rated',
  ).length;

  // -- I. Executive summary --------------------------------------------------
  const fragSentence =
    s.trueConsolidation === 'Low'
      ? `Corporate consolidation in the segment remains low, and the acquirable-independent pool is correspondingly deep`
      : s.trueConsolidation === 'Moderate'
        ? `The segment is partially consolidated, but a substantial independent tail remains acquirable`
        : `The segment is already heavily consolidated, which narrows the acquirable tail and raises the premium on target selection`;
  const visSentence =
    s.visibility === 'masked'
      ? `ownership visibility in the data is masked — consolidators operating under retained local names will not all be screened out, and the pool should be read as an upper bound`
      : s.visibility === 'partial'
        ? `ownership visibility is partial, so a minority of listed independents may prove consolidator-owned on contact`
        : `ownership visibility is high, so the independent pool is well screened`;
  const execP1 = `${meta.country} ${meta.subsegment.toLowerCase()} presents a ${
    s.feasible ? 'buildable' : 'supply-constrained'
  } buy-and-build opportunity in ${geo}. ${fragSentence}: the eligible universe runs to ${s.poolSize.toLocaleString(
    'en-GB',
  )} independent operators carrying ${money(s.poolRevEur)} of combined revenue, with a median operator size of ${money(
    s.poolMedianEur,
  )}. However, ${visSentence}.`;

  const anchorSentence = anchor
    ? `The build is anchor-led: ${anchor.name ?? 'the selected platform'} (${money(
        anchor.turnoverEur,
      )}) provides the operating nucleus, with ${boltOns.length} bolt-${plural(boltOns.length, 'on')} filling to target`
    : s.anchorFallback
      ? `Anchor-led construction was specified but no independent of the required scale exists in the pool; the build therefore proceeds as an aggregation of comparable operators`
      : `The build is an aggregation: ${n} comparable operators${
          boltMedian != null ? ` (median ${money(boltMedian)})` : ''
        } assembled as a cluster rather than around a single anchor`;
  const feasSentence = s.feasible
    ? `The proposed buy-list assembles ${money(s.assembledEur)} — ${fillPct}% of the ${money(s.targetEur)} mandate — across ${regions.length} ${plural(regions.length, 'region')}`
    : `The pool cannot currently reach the stated target: ${money(s.buildableRevEur)} is assemblable against ${money(s.targetEur)}, and the committee should treat this memorandum as sizing the available opportunity rather than the requested one`;
  const execP2 = `${anchorSentence}. ${feasSentence}.`;

  const gettableLine =
    avgSeller != null && successionRich.length > 0
      ? `Owner dynamics favour the buyer: seller readiness averages ${avgSeller}/100 across the list and ${successionRich.length} of ${n} ${plural(n, 'target')} carry structurally strong owner-transition signals.`
      : avgSeller != null
        ? `Seller readiness averages ${avgSeller}/100 across the list.`
        : '';
  const buyerLine = buyerAware
    ? gapTargets.length > 0 && overlapTargets.length === 0
      ? `Against the attached footprint the build is additive — ${gapTargets.length} ${plural(gapTargets.length, 'target extends', 'targets extend')} the buyer into geography it does not currently operate, with no catchment overlap identified at region grain.`
      : overlapTargets.length > 0
        ? `Against the attached footprint, ${overlapTargets.length} ${plural(overlapTargets.length, 'target overlaps', 'targets overlap')} existing operations and should be individually justified on density grounds.`
        : `The attached footprint neither overlaps nor is extended by this build at region grain.`
    : `No buyer context is attached; fit statements below describe the build itself, not a specific acquirer.`;
  const recommendPreview = s.feasible
    ? `On the evidence assembled below, the recommendation is to proceed to sequenced owner outreach, subject to the diligence plan in §VIII.`
    : `On the evidence assembled below, the recommendation is to re-scope (wider geography, wider size band, or a lower target) before committing outreach resources.`;
  const exec = `${execP1}\n\n${execP2}\n\n${gettableLine} ${buyerLine} ${recommendPreview}`;

  // -- II. Investment thesis (pillars render only when evidenced) -------------
  const pillars: Array<{ title: string; body: string }> = [];
  if (s.feasible) {
    pillars.push({
      title: anchor ? 'A credible platform nucleus' : 'A coherent cluster at target scale',
      body: anchor
        ? `${anchor.name ?? 'The anchor'} anchors the build at ${money(anchor.turnoverEur)}${
            anchor.qualityTier && anchor.qualityTier !== 'Not rated'
              ? `, carrying a ${anchor.qualityTier.toLowerCase()} regulatory-quality tier`
              : ''
          }. The remaining ${money(s.assembledEur - (anchor.turnoverEur ?? 0))} of the build sits under it in ${boltOns.length} bolt-${plural(boltOns.length, 'on')}, none larger than the anchor — a structure that can integrate sequentially rather than all at once.`
        : `The buy-list assembles as a genuine cluster — ${n} operators${boltMedian != null ? ` around a ${money(boltMedian)} median` : ''} — so no single failed process breaks the build; each member is individually replaceable from a pool of ${s.poolSize.toLocaleString('en-GB')}.`,
    });
  }
  if (successionRich.length > 0) {
    const names = listJoin(successionRich.slice(0, 3).map((t) => t.name ?? '—'));
    pillars.push({
      title: 'Succession-driven availability',
      body: `${successionRich.length} of ${n} ${plural(n, 'target')} (${names}${successionRich.length > 3 ? ' among others' : ''}) show unusually strong owner-transition signals${avgSeller != null ? `; readiness averages ${avgSeller}/100 across the list` : ''}. These are structural reads — ownership tenure, owner age markers, absence of succession — not intent-to-sell claims, but they materially raise the odds that priced, respectful approaches convert.`,
    });
  }
  if (buyerAware && gapTargets.length > 0) {
    pillars.push({
      title: 'Footprint extension, not duplication',
      body: `${gapTargets.length} ${plural(gapTargets.length, 'target adds', 'targets add')} geography the attached portfolio does not cover (${listJoin([...new Set(gapTargets.map((t) => t.region).filter((r): r is string => !!r))])}). ${overlapTargets.length === 0 ? 'No owned-region overlap was identified, so the build buys reach rather than density.' : `${overlapTargets.length} ${plural(overlapTargets.length, 'target')} overlap the existing footprint and should be held to a density thesis.`}`,
    });
  }
  if (localChampions.length > 0 || hiddenPlatforms.length > 0) {
    const bits: string[] = [];
    if (localChampions.length > 0)
      bits.push(
        `${localChampions.length} ${plural(localChampions.length, 'is a local champion', 'are local champions')} — regionally meaningful operators trading well above the pool median`,
      );
    if (hiddenPlatforms.length > 0)
      bits.push(
        `${hiddenPlatforms.length} ${plural(hiddenPlatforms.length, 'is a hidden platform', 'are hidden platforms')} — multi-site operators behaving like platforms before being priced as one`,
      );
    pillars.push({
      title: 'Quality of the individual assets',
      body: `Within the buy-list, ${listJoin(bits)}. These targets carry disproportionate strategic value relative to their size band and should lead the outreach sequence.`,
    });
  }
  if (pillars.length === 0) {
    pillars.push({
      title: 'A thin thesis, stated honestly',
      body: 'The evidence assembled does not support a strong structural thesis for this build beyond target-count arithmetic. The committee should weigh the diligence plan and market backdrop below before committing resources.',
    });
  }

  // -- III. Market backdrop ----------------------------------------------------
  const market = `The consolidation benchmark for ${meta.subsegment.toLowerCase()} reads ${s.trueConsolidation.toLowerCase()} true consolidation with ${s.visibility} visibility in our ownership data (${s.benchmarkBasis}). ${s.benchmarkNote} Pool confidence for this build is therefore rated ${s.confidence}. ${
    s.qualitySignalAvailable
      ? `A regulatory-quality signal exists for this market: ${weakCount > 0 ? `${weakCount} ${plural(weakCount, 'target')} in the list ${plural(weakCount, 'is', 'are')} rated weak` : 'no weak-rated operator remains in the list'}${unratedCount > 0 ? `, while ${unratedCount} ${plural(unratedCount, 'carries', 'carry')} no rating` : ''}.`
      : 'No regulatory-quality signal covers this market/subsegment; quality must be established in diligence rather than screened.'
  }`;

  // -- IV. Structure & sequencing ----------------------------------------------
  const structure = anchor
    ? `Execution should proceed anchor-first: secure ${anchor.name ?? 'the anchor'} before any bolt-on approach, since the bolt-on economics depend on the platform existing. Bolt-ons then sequence by seller readiness${avgSeller != null ? ` (list average ${avgSeller}/100)` : ''}, highest first, so early wins fund the pipeline's patience.`
    : `As an aggregation, sequencing is readiness-led: approach the most gettable owners first${successionRich.length > 0 ? ` — the ${successionRich.length} succession-rich ${plural(successionRich.length, 'target')} above` : ''} — and treat every member as replaceable from the wider pool. ${s.comparability ? `Members were admitted within a ${money(s.comparability.loEur)}–${s.comparability.hiEur != null ? money(s.comparability.hiEur) : 'unbounded'} comparability band around ${money(s.comparability.centerEur)}, so the cluster reads as a set of peers rather than an anchor in disguise.` : ''}`;
  const burden = s.oversized
    ? `Execution burden. At ${s.dealCount} acquisitions this build sits above the typical single-hold pattern for the subsegment (${s.typicalBuildLabel}). The committee should either accept a multi-year integration programme or instruct a re-scope toward fewer, larger assets.`
    : '';

  // -- V. Per-target profiles ----------------------------------------------------
  const basisShort = (p: PlatformBuilderItem) =>
    isReportedBasis(p.turnoverBasis) ? 'filed' : `modelled ${p.turnoverConfidenceP ?? ''}`.trim();
  const targetMeta = new Map<string, string>();
  const targetTexts = new Map<string, string>();
  for (const p of targets) {
    targetMeta.set(p.entityId, `(${p.region ?? '—'} · ${money(p.turnoverEur)} ${basisShort(p)})`);
    const conds = p.conditions ?? [];
    const pos = conds.filter((c) => c.tone === 'positive').map((c) => stripDot(c.note));
    const risks = conds.filter((c) => c.tone === 'risk').map((c) => stripDot(c.note));
    targetTexts.set(
      p.entityId,
      [
        pos.length ? pos.join('; ') : 'no evidenced conditions beyond its place in the fill',
        ...risks.map((r) => `Risk: ${r}`),
      ].join('. ') + '.',
    );
  }

  // -- VI. Financial profile ----------------------------------------------------
  const finance = `The build's ${money(s.assembledEur)} of combined revenue is ${
    reportedCount === n
      ? 'entirely filed'
      : reportedCount === 0
        ? 'entirely modelled'
        : `${reportedCount} ${plural(reportedCount, 'part')} filed, ${modelledCount} ${plural(modelledCount, 'part')} modelled`
  }${
    modelledCount > 0
      ? '. Modelled figures are screening-grade estimates calibrated to a ±50% band at the stated P-confidence; they size the opportunity but do not price any individual deal'
      : ''
  }. Average origination signal across the list is ${avgOrig ?? '—'}/100 (within-market percentile)${anchor && boltMedian != null ? `; the anchor's ${money(anchor.turnoverEur)} sits against a ${money(boltMedian)} median bolt-on` : ''}.`;

  // -- IX. Recommendation -----------------------------------------------------------
  const riskCount = countRiskRows(result, condSummary.risks.length);
  const recommendation = s.feasible
    ? `Proceed. The buy-list assembles ${fillPct}% of the mandate from ${n} ${plural(n, 'operator')} with ${
        condSummary.positives.length
      } evidenced positive ${plural(condSummary.positives.length, 'condition')} and ${riskCount} managed ${plural(riskCount, 'risk')}. Recommended next actions: (1) ${anchor ? `open the anchor conversation with ${anchor.name ?? 'the anchor'}` : 'open outreach with the highest-readiness targets'}; (2) commission the §VIII diligence items that can run pre-outreach; (3) commit the build to a Roll-Up Foundation for tracking${buyerAware ? '' : '; (4) attach a mandate and portfolio and re-run for buyer-relative fit before committee'}.`
    : `Do not proceed at the stated target. The pool supports ${money(s.buildableRevEur)}; either re-scope the build (wider geography or size band) or reset expectations to the assemblable figure and re-run Prescription.`;

  // -- assemble ----------------------------------------------------------------
  const narrative = new Map<string, string>([
    ['exec', exec],
    ...pillars.map((p, i) => [`pillar:${i}`, p.body] as [string, string]),
    ['market', market],
    ['structure', structure],
    ['finance', finance],
    ['recommendation', recommendation],
    ...targets.map(
      (p) => [`target:${p.entityId}`, targetTexts.get(p.entityId)!] as [string, string],
    ),
  ]);
  const sections = [...narrative.entries()].map(([key, text]) => ({ key, text }));

  // The fact corpus = every deterministic section + every evidence string the
  // conditions carry + the structural facts (stat values, target meta lines).
  // Cross-section figures are legal precisely because all sections are facts.
  const allConditions: Condition[] = [
    ...buildConditions,
    ...targets.flatMap((p) => p.conditions ?? []),
  ];
  const factCorpus = [
    ...sections.map((x) => x.text),
    burden,
    ...allConditions.flatMap((c) => [
      c.note,
      ...c.evidence.map((e) => `${e.label}: ${e.value}`),
      ...c.caveats,
    ]),
    ...[...targetMeta.entries()].map(([id, m]) => {
      const t = targets.find((p) => p.entityId === id);
      return `${t?.name ?? id} ${m}`;
    }),
    `Target ${money(s.targetEur)} · assembled ${money(s.assembledEur)} (${fillPct}%) · operators ${n} · regions ${regions.length} (${regions.join(', ')}) · pool ${s.poolSize.toLocaleString('en-GB')} operators ${money(s.poolRevEur)} · pool median ${money(s.poolMedianEur)} · avg origination ${avgOrig ?? '—'} · avg seller readiness ${avgSeller ?? '—'} · confidence ${s.confidence}`,
    s.benchmarkNote,
    s.benchmarkBasis,
  ].filter((f) => f && f.trim().length > 0);

  return {
    result,
    meta,
    ccy,
    geo,
    narrative,
    pillarTitles: pillars.map((p) => p.title),
    burden,
    targetMeta,
    factCorpus,
    allowedNames: targets.map((t) => t.name).filter((x): x is string => !!x),
    sections,
  };
}

/** Risk-row count shared by the recommendation text and the risks table so the
 *  memo can't disagree with itself. */
function countRiskRows(result: PlatformBuilderResult, conditionRisks: number): number {
  return buildRiskRows(result, []).length + conditionRisks;
}

function buildRiskRows(
  result: PlatformBuilderResult,
  conditionCaveats: string[],
): Array<[string, string]> {
  const s = result.summary;
  const n = result.portfolio.length;
  const riskRows: Array<[string, string]> = [];
  const caveatMitigant: Array<[RegExp, string, string]> = [
    [
      /modelled/i,
      'Revenue provenance',
      'Obtain filed accounts or management figures in first diligence pass; treat modelled figures as sizing only.',
    ],
    [
      /region grain|site catchments/i,
      'Overlap measured coarsely',
      'Re-run overlap at site level (drive-time catchments) before final buy-list sign-off.',
    ],
    [
      /regulated site|footprint unverified/i,
      'Unverified physical footprint',
      'Verify sites for office-registered targets before valuing reach.',
    ],
    [
      /portfolio.*incomplete/i,
      'Owned footprint may be incomplete',
      'Confirm the attached portfolio lists every operating site; gap/overlap reads depend on it.',
    ],
    [
      /value trap/i,
      'Weak quality may be structural',
      'Inspect regulatory history early; underwrite the turnaround only with a named operating fix.',
    ],
    [
      /heuristic/i,
      'Specialism detection is heuristic',
      'Corroborate detected specialisms against actual service lines on first contact.',
    ],
    [
      /intent-to-sell/i,
      'Readiness ≠ willingness',
      'Calibrate approach expectations: readiness is structural; no owner has signalled a sale.',
    ],
    [
      /quality signal/i,
      'No quality screen available',
      'Substitute local inspection/registration evidence for the missing regulatory signal.',
    ],
  ];
  const seen = new Set<string>();
  for (const cav of conditionCaveats) {
    const hit = caveatMitigant.find(([re]) => re.test(cav));
    if (hit && !seen.has(hit[1])) {
      seen.add(hit[1]);
      riskRows.push([hit[1] + ' — ' + stripDot(cav), hit[2]]);
    }
  }
  const money = (eur: number) => `€${(eur / 1e6).toFixed(1)}m`;
  if (!s.feasible)
    riskRows.push([
      `Inventory constraint — ${money(s.buildableRevEur)} assemblable vs ${money(s.targetEur)} target`,
      'Widen region or size band, or reset the target to the assemblable figure.',
    ]);
  if (s.oversized)
    riskRows.push([
      `Execution burden — ${s.dealCount} acquisitions vs typical ${s.typicalBuildLabel}`,
      'Stage the programme in tranches; re-run Prescription after each tranche completes.',
    ]);
  if ((s.confidence ?? '').toLowerCase() === 'low')
    riskRows.push([
      'Pool confidence low — hidden consolidation may shrink the true pool',
      'Discount the pool mentally by the benchmark note and validate independence on first contact.',
    ]);
  // Suppress the modelled-count duplicate note: covered by the caveat class.
  void n;
  return riskRows;
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderHtml(
  composed: ComposedMemo,
  overrides: Map<string, string> | null,
  opts: { drafting: boolean; model?: string },
): string {
  const { result, meta, ccy, geo } = composed;
  const s = result.summary;
  const targets = result.portfolio;
  const n = targets.length;
  const money = (eur: number | null | undefined) =>
    eur == null ? '—' : formatMoney(convertFromEur(eur, ccy), { currency: CURRENCY_SYMBOL[ccy] });
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const fillPct = Math.round((s.assembledEur / Math.max(1, s.targetEur)) * 100);
  const regions = [...new Set(targets.map((p) => p.region).filter((r): r is string => !!r))];
  const buyerAware = meta.contextMode === 'buyer_aware';
  const anchor = targets.find((p) => p.role === 'platform') ?? null;
  const avgSeller = avgOf(targets.map((p) => p.sellerReadiness));
  const avgOrig = avgOf(targets.map((p) => p.originationSignal));

  const text = (key: string): string => overrides?.get(key) ?? composed.narrative.get(key) ?? '';
  const draftedCount = overrides?.size ?? 0;

  const buildConditions = result.conditions ?? [];
  const condSummary = summariseConditions(buildConditions);
  const allConditions: Condition[] = [
    ...buildConditions,
    ...targets.flatMap((p) => p.conditions ?? []),
  ];
  const conditionCaveats = [...new Set(allConditions.flatMap((c) => c.caveats))];
  const riskRows: Array<[string, string]> = [
    ...condSummary.risks.map(
      (r) =>
        [
          stripDot(r.note),
          'Justify the overlapping target(s) on a density thesis, or replace from the pool.',
        ] as [string, string],
    ),
    ...buildRiskRows(result, conditionCaveats),
  ];
  const riskTable = riskRows.length
    ? `<table><thead><tr><th style="width:55%">Risk</th><th>Mitigant</th></tr></thead><tbody>${riskRows
        .map(([r, m]) => `<tr><td>${esc(r)}</td><td>${esc(m)}</td></tr>`)
        .join('')}</tbody></table>`
    : '<div class="muted">No material risks were generated from the assembled evidence.</div>';

  const diligence = [
    ...new Set([
      ...riskRows.map(([, m]) => m),
      'Validate latest filed accounts, revenue bridge, and any modelled revenue assumptions.',
      'Confirm ownership and group structure — no target should prove consolidator-owned.',
      'Review regulatory/inspection history for each target before outreach.',
      'Confirm acquisition sequencing and integration capacity against the buyer mandate.',
    ]),
  ];
  const diligenceHtml = diligence.map((d) => `<li>${esc(d)}</li>`).join('');

  const preparedFor = buyerAware
    ? `Prepared for ${[
        meta.mandateName ? `mandate “${meta.mandateName}”` : null,
        meta.portfolioName ? `portfolio “${meta.portfolioName}”` : null,
      ]
        .filter(Boolean)
        .join(' · ')}`
    : 'Prepared from the Prescription build context — no buyer mandate or portfolio attached';

  const qualityLabel = (q: string | null | undefined) => q ?? 'Not rated';
  const basisShort = (p: PlatformBuilderItem) =>
    isReportedBasis(p.turnoverBasis) ? 'filed' : `modelled ${p.turnoverConfidenceP ?? ''}`.trim();
  const rows = targets
    .map(
      (p) => `<tr${p.role === 'platform' ? ' class="me"' : ''}>
        <td>${esc(p.name ?? '—')}</td>
        <td>${p.role === 'platform' ? 'Anchor' : 'Bolt-on'}</td>
        <td>${esc(p.region ?? '—')}</td>
        <td class="num">${money(p.turnoverEur)}</td>
        <td>${esc(basisShort(p))}</td>
        <td>${esc(qualityLabel(p.qualityTier))}</td>
        <td class="num">${p.originationSignal ?? '—'}</td>
        <td class="num">${p.sellerReadiness ?? '—'}</td>
      </tr>`,
    )
    .join('');
  const profileLis = targets
    .map(
      (p) =>
        `<li><b>${esc(p.name ?? '—')}</b> <span class="muted">${esc(
          composed.targetMeta.get(p.entityId) ?? '',
        )}</span> — ${esc(text(`target:${p.entityId}`)).replace(/Risk: ([^.]+\.)/g, '<span class="neg">Risk: $1</span>')}</li>`,
    )
    .join('');

  const objLabels: Record<string, string> = {
    gettability: 'gettability',
    fewestDeals: 'fewest deals',
    tightestCluster: 'tightest cluster',
    diversifyRegion: 'regional spread',
  };
  const objectiveText = meta.objectives.map((o) => objLabels[o] ?? o).join(', ');

  const stat = (l: string, v: string, sub = '') =>
    `<div class="stat"><div class="stat-l">${esc(l)}</div><div class="stat-v">${v}</div>${sub ? `<div class="stat-s">${esc(sub)}</div>` : ''}</div>`;
  const stats = [
    stat('Target', money(s.targetEur)),
    stat('Assembled', money(s.assembledEur), `${fillPct}% of target`),
    stat('Operators', String(n), anchor ? 'anchor-led' : 'aggregation'),
    stat('Regions', String(regions.length), s.coreRegion ?? ''),
    stat('Avg origination', avgOrig != null ? `${avgOrig}` : '—', 'of 100, within-market'),
    stat('Avg seller readiness', avgSeller != null ? `${avgSeller}` : '—', 'of 100'),
    stat('Pool depth', s.poolSize.toLocaleString('en-GB'), money(s.poolRevEur)),
    stat('Pool confidence', s.confidence, 'consolidation benchmark'),
  ].join('');

  const draftPill = opts.drafting
    ? `<div id="draftpill" class="draftpill">Drafting narrative…</div>`
    : '';
  const narrativeFoot =
    draftedCount > 0
      ? ` Narrative sections were drafted by ${esc(opts.model ?? 'the model')} over the deterministic evidence and machine-verified: every figure and company name was checked against the fact corpus, and any section failing verification retains its standard wording.`
      : ' Every statement is assembled deterministically from platform evidence — conditions, canonical scores, and the consolidation benchmark; no generative claims.';

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
<title>IC Memorandum · ${esc(meta.subsegment)} · ${esc(geo)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap');
  /* Corryk paper-sheet grammar — same tokens as the Target Dossier. */
  :root{
    --ink:#1a1410;--mid:#424a53;--muted:#6e7681;--line:#d6c9b8;
    --accent:#a8572a;--positive:#0a7d5a;--negative:#c0143c;
    --canvas:#fff1e5;--paper:#ffffff;--elevated:#f7ecdc;
    --font-ui:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
    --font-ed:'Source Serif 4','Source Serif Pro',Georgia,'Times New Roman',serif;
  }
  *{box-sizing:border-box;}
  html,body{margin:0;background:var(--canvas);color:var(--ink);font:11px/1.45 var(--font-ui);}
  .sheet{max-width:820px;margin:12px auto;background:var(--paper);padding:20px 26px 14px;box-shadow:0 1px 6px rgba(60,40,20,.16);}
  .brandbar{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid var(--accent);padding-bottom:4px;margin-bottom:8px;}
  .brand{font-family:var(--font-ed);font-weight:600;letter-spacing:.01em;color:var(--accent);font-size:14px;}
  .brand small{font-family:var(--font-ui);color:var(--muted);font-weight:500;letter-spacing:0;font-size:10.5px;}
  .meta{font-size:10px;color:var(--muted);text-align:right;}
  .conf{color:var(--negative);font-weight:600;letter-spacing:.04em;}
  h1{font-family:var(--font-ed);font-size:20px;font-weight:600;letter-spacing:-.01em;margin:6px 0 1px;line-height:1.12;}
  .sub{font-size:11px;color:var(--muted);margin-bottom:10px;}
  .grid{display:grid;grid-template-columns:repeat(8,1fr);gap:5px;margin:8px 0 4px;}
  .stat{border:1px solid var(--line);border-radius:4px;padding:4px 6px;background:var(--paper);display:flex;flex-direction:column;}
  .stat-l{font-size:8px;color:var(--muted);text-transform:uppercase;letter-spacing:.02em;line-height:1.12;min-height:18px;}
  .stat-v{font-size:13px;font-weight:600;font-variant-numeric:tabular-nums;line-height:1.15;}
  .stat-s{font-size:8px;color:var(--muted);}
  h2{font-family:var(--font-ui);font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;color:var(--accent);border-bottom:1px solid var(--line);padding-bottom:2px;margin:14px 0 6px;}
  p{margin:0 0 7px;font-size:11.5px;line-height:1.52;}
  .lede p{font-family:var(--font-ed);font-size:12.5px;line-height:1.55;}
  .pillar{margin:0 0 8px;}
  .pillar b.pt{display:block;font-size:11.5px;margin-bottom:1px;}
  .cols{display:grid;grid-template-columns:1fr 300px;gap:18px;align-items:start;}
  table{width:100%;border-collapse:collapse;font-size:10.5px;}
  td,th{padding:2.6px 5px;border-bottom:1px solid var(--line);text-align:left;vertical-align:top;}
  th{font-size:9px;text-transform:uppercase;color:var(--muted);letter-spacing:.03em;}
  .num{text-align:right;font-variant-numeric:tabular-nums;}
  tr.me td{background:var(--elevated);font-weight:600;}
  .muted{color:var(--muted);}
  .neg{color:var(--negative);}
  ul{margin:4px 0 6px 16px;padding:0;}
  li{margin:0 0 4px;font-size:11px;line-height:1.5;}
  ol{margin:4px 0 6px 18px;padding:0;}
  ol li{margin:0 0 4px;}
  .note{font-size:9.5px;color:var(--muted);margin-top:4px;}
  .printbar{position:fixed;top:14px;right:14px;}
  .printbar button{font:600 13px var(--font-ui);background:var(--accent);color:#fff;border:0;border-radius:6px;padding:8px 14px;cursor:pointer;box-shadow:0 1px 4px rgba(60,40,20,.25);}
  .draftpill{position:fixed;top:16px;left:16px;font:600 11px var(--font-ui);color:var(--muted);background:var(--elevated);border:1px solid var(--line);border-radius:12px;padding:4px 10px;}
  .foot{margin-top:12px;padding-top:6px;border-top:1px solid var(--line);font-size:9px;color:var(--muted);line-height:1.45;}
  @page{size:A4;margin:11mm;}
  @media print{html,body{background:#fff;}.sheet{box-shadow:none;margin:0;max-width:none;padding:0;}.printbar,.draftpill{display:none;}h2,table,.grid,.cols,.pillar{page-break-inside:avoid;}}
</style></head>
<body>
  <div class="printbar"><button onclick="window.print()">Save as PDF / Print</button></div>
  ${draftPill}
  <div class="sheet">
    <div class="brandbar">
      <div class="brand">Corryk <small>· Investment Committee Memorandum</small></div>
      <div class="meta"><span class="conf">CONFIDENTIAL — DRAFT FOR DISCUSSION</span><br/>${esc(today)}</div>
    </div>
    <h1>Project ${esc(meta.subsegment)} — ${esc(geo)} buy-and-build</h1>
    <div class="sub">${esc(preparedFor)} · construction objectives: ${esc(objectiveText)} · ${esc(meta.anchorMode)}</div>

    <div class="grid">${stats}</div>

    <h2>I · Executive summary</h2>
    <div class="lede">${paras(text('exec'))}</div>

    <h2>II · Investment thesis</h2>
    ${composed.pillarTitles
      .map(
        (title, i) =>
          `<div class="pillar"><b class="pt">${esc(title)}</b>${paras(text(`pillar:${i}`))}</div>`,
      )
      .join('')}

    <div class="cols">
      <div>
        <h2>III · Market backdrop</h2>
        ${paras(text('market'))}
        <h2>IV · Structure &amp; sequencing</h2>
        ${paras(text('structure'))}
        ${composed.burden ? `<p><b>${esc(composed.burden)}</b></p>` : ''}
      </div>
      <div>
        <h2>Footprint</h2>
        ${mapBlock(result)}
      </div>
    </div>

    <h2>V · Target portfolio — ${n} ${plural(n, 'operator')}</h2>
    <table>
      <thead><tr><th>Company</th><th>Role</th><th>Region</th><th class="num">Revenue</th><th>Basis</th><th>Quality</th><th class="num">Orig.</th><th class="num">Seller</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <ul style="margin-top:7px">${profileLis}</ul>

    <h2>VI · Financial profile</h2>
    ${paras(text('finance'))}

    <h2>VII · Risks &amp; mitigants</h2>
    ${riskTable}

    <h2>VIII · Diligence plan</h2>
    <ol>${diligenceHtml}</ol>

    <h2>IX · Recommendation</h2>
    ${paras(text('recommendation'))}

    <div class="foot">${narrativeFoot} Revenue and scores are <b>modelled indicators for screening, not audited figures or valuation advice</b>; modelled revenue carries a P-band (calibrated probability the true figure is within ±50%). Conditions are evidence-bound: each carries its evidence and caveats in-app. Figures in ${esc(ccy)} where convertible. Assessment mode: ${buyerAware ? `buyer-aware${meta.mandateName ? ` · mandate “${esc(meta.mandateName)}”` : ''}${meta.portfolioName ? ` · portfolio “${esc(meta.portfolioName)}”` : ''}` : 'build form context (no buyer attached)'}. © Corryk.</div>
  </div>
</body></html>`;
}
